import { useId, useState, type FormEvent } from 'react';
import { betragAusFeld, betragFeld } from './logik';
import type { BankkontoSicht } from './types';

// Werte einer Zeile von Hand oder einer Korrektur (Datum, Betrag mit Vorzeichen, Name, Zweck)
export interface ZeileWerte {
  bankkontoId?: string;
  datum: string;
  betragCents: number;
  name: string | null;
  zweck: string;
}

export interface ZeileStart {
  bankkontoId?: string | null;
  datum?: string | null;
  betragCents?: number | null;
  name?: string | null;
  zweck?: string | null;
}

// Formular für eine Zeile: „Zeile von Hand“ (mit Bankkonto, wenn mehrere) oder „Datum oder Betrag ändern“.
// Der Betrag steht ohne Vorzeichen; Geld rein oder raus wählt man daneben. Fehler stehen unter dem Formular.
export function ZeileFormular({
  titel,
  knopf,
  start,
  konten,
  erster,
  letzter,
  busy,
  fehler,
  onSenden,
  onAbbrechen,
}: {
  titel: string;
  knopf: string;
  start: ZeileStart;
  konten?: BankkontoSicht[]; // nur bei einer neuen Zeile
  erster: string; // JJJJ-MM-TT, erster Tag des Monats
  letzter: string;
  busy: boolean;
  fehler: string | null;
  onSenden(w: ZeileWerte): void;
  onAbbrechen(): void;
}) {
  const id = useId();
  const [konto, setKonto] = useState(start.bankkontoId ?? konten?.[0]?.id ?? '');
  const [datum, setDatum] = useState(start.datum ?? '');
  const [raus, setRaus] = useState((start.betragCents ?? -1) < 0);
  const [betrag, setBetrag] = useState(start.betragCents ? betragFeld(Math.abs(start.betragCents), 1) : '');
  const [name, setName] = useState(start.name ?? '');
  const [zweck, setZweck] = useState(start.zweck ?? '');
  const [eigen, setEigen] = useState<string | null>(null);

  const senden = (e: FormEvent) => {
    e.preventDefault();
    const cents = betragAusFeld(betrag.replace(/^[-−–+]/, ''), 1);
    if (!datum || datum < erster || datum > letzter) return setEigen('Bitte ein Datum in diesem Monat wählen.');
    if (!cents) return setEigen('Bitte den Betrag eintragen, z. B. 180,00.');
    if (!zweck.trim()) return setEigen('Bitte den Verwendungszweck eintragen.');
    if (konten && !konto) return setEigen('Bitte das Bankkonto wählen.');
    setEigen(null);
    onSenden({
      ...(konten ? { bankkontoId: konto } : {}),
      datum,
      betragCents: raus ? -Math.abs(cents) : Math.abs(cents),
      name: name.trim() || null,
      zweck: zweck.trim(),
    });
  };
  const satz = eigen ?? fehler;

  return (
    <form className="zf" onSubmit={senden} aria-labelledby={`${id}-h`}>
      <h3 id={`${id}-h`}>{titel}</h3>
      <div className="zf-r">
        {konten && konten.length > 1 && (
          <label>
            Bankkonto
            <select value={konto} onChange={(e) => setKonto(e.target.value)}>
              {konten.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.finanzkonto} {k.bezeichnung}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Datum
          <input type="date" value={datum} min={erster} max={letzter} required onChange={(e) => setDatum(e.target.value)} />
        </label>
        <div className="zf-art seg" role="group" aria-label="Geld rein oder raus">
          <button type="button" aria-pressed={raus} onClick={() => setRaus(true)}>
            Geld raus
          </button>
          <button type="button" aria-pressed={!raus} onClick={() => setRaus(false)}>
            Geld rein
          </button>
        </div>
        <label>
          Betrag in €
          <input className="num" value={betrag} inputMode="decimal" placeholder="0,00" required onChange={(e) => setBetrag(e.target.value)} />
        </label>
      </div>
      <div className="zf-r">
        <label className="zf-w">
          Name
          <input value={name} maxLength={200} placeholder="Empfänger oder Auftraggeber" onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="zf-w2">
          Verwendungszweck
          <input value={zweck} maxLength={500} required onChange={(e) => setZweck(e.target.value)} />
        </label>
      </div>
      {satz && (
        <p className="zd-err" role="alert">
          {satz}
        </p>
      )}
      <div className="zd-act">
        <button type="submit" className="btn sm dark" disabled={busy}>
          {busy ? 'Speichert …' : knopf}
        </button>
        <button type="button" className="btn sm" disabled={busy} onClick={onAbbrechen}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}
